import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Hours, Organization } from 'models/organization';

type OpeningHoursProps = {
    /** DD-3696: only the opening hours are read, so Find lab can pass its directory lab. */
    currentLab: Pick<Organization, 'openingHours'>;
};

const OpeningHours: FC<OpeningHoursProps> = ({ currentLab }) => {
    const { t: translate } = useTranslation(['lab-settings', 'organization']);

    const formatTime = (timestamp: number) =>
        new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const getDayAbbreviation = (dayName: string) => translate(`organization:${dayName.toUpperCase()}`).slice(0, 3);

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                {translate('organization:OPENING_HOURS')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {currentLab.openingHours.map((hours: Hours, dayIndex: number) => (
                    <Box
                        key={dayIndex}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            minWidth: 68,
                            backgroundColor: hours.active ? 'primary.50' : 'grey.100',
                            border: '1px solid',
                            borderColor: hours.active ? 'primary.200' : 'grey.300',
                            opacity: hours.active ? 1 : 0.6,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.68rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            {getDayAbbreviation(hours.day)}
                        </Typography>
                        {hours.active ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                                    {formatTime(hours.time.from)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '0.6rem', color: 'text.disabled', lineHeight: 1 }}
                                >
                                    –
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                                    {formatTime(hours.time.to)}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.5 }}
                            >
                                {translate('lab-settings:CLOSED')}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default OpeningHours;
